import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Landmark, User2, Loader2, ShieldCheck } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

function fmtErr(detail) {
  if (!detail) return "లోపం జరిగింది. మళ్ళీ ప్రయత్నించండి.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  if (detail.message) return detail.message;
  return String(detail);
}

export default function Auth() {
  const { t } = useApp();
  const { user, login, register } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [mode, setMode] = useState(sp.get("mode") === "signup" ? "signup" : "login");
  const next = sp.get("next") || "/dashboard";
  const [role, setRole] = useState("temple");
  const [form, setForm] = useState({ name: "", email: "", password: "", mobile: "" });
  const [busy, setBusy] = useState(false);
  const otpStage = false;

  useEffect(() => { if (user) nav(next); }, [user]);
  useEffect(() => { setMode(sp.get("mode") === "signup" ? "signup" : "login"); }, [sp]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const googleLogin = () => {
    localStorage.setItem("db_pending_role", role);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const doLogin = async () => {
    setBusy(true);
    try { await login(form.email, form.password); toast.success("స్వాగతం! 🙏"); nav(next); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const startSignup = async () => {
    if (!form.name || !form.email || !form.password) return toast.error("అన్ని వివరాలు నింపండి");
    if (form.mobile && !/^\d{10}$/.test(form.mobile)) return toast.error("సరైన 10 అంకెల మొబైల్ నంబర్ నమోదు చేయండి");
    setBusy(true);
    try { await register({ ...form, role }); toast.success("ఖాతా సృష్టించబడింది! 🎉"); nav(next); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const RoleBtn = ({ value, icon: Icon, label, sub }) => (
    <button type="button" data-testid={`role-${value}`} onClick={() => setRole(value)}
      className={`flex-1 p-4 rounded-xl border-2 text-left transition-all ${role === value ? "border-saffron bg-marigold/15" : "border-goldline/40 hover:bg-marigold/5"}`}>
      <Icon className={`w-6 h-6 mb-2 ${role === value ? "text-saffron" : "text-maroon"}`} />
      <div className="font-semibold text-maroon font-telugu text-sm">{label}</div>
      <div className="text-[11px] text-muted-foreground font-telugu">{sub}</div>
    </button>
  );

  return (
    <div className="parchment-texture min-h-screen flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md bg-surface rounded-2xl temple-shadow gold-border p-6 sm:p-8 animate-fade-up">
        <div className="text-center mb-6">
          <div className="text-4xl">🔔</div>
          <h1 className="font-display text-2xl font-extrabold text-maroon mt-2">{mode === "login" ? t("login") : t("signup")}</h1>
          <p className="text-xs text-saffron font-telugu">{t("site_name")}</p>
        </div>

        {mode === "signup" && !otpStage && (
          <div className="mb-4">
            <Label className="font-telugu text-maroon">{t("select_role")}</Label>
            <div className="flex gap-2 mt-2">
              <RoleBtn value="temple" icon={Landmark} label={t("role_temple")} sub="5 ఉచిత క్రెడిట్స్" />
              <RoleBtn value="archaka" icon={User2} label={t("role_archaka")} sub="ఆమోదం అవసరం" />
            </div>
          </div>
        )}

        {!otpStage && (
          <div className="mb-4 space-y-3">
            <Button data-testid="google-signin-top" onClick={googleLogin} variant="outline" className="w-full h-11 border-goldline/60 font-telugu bg-white">
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38Z"/></svg>
              మీ ఫోన్‌లోని Gmail తో {mode === "login" ? "లాగిన్" : "ఖాతా తెరవండి"} (OTP అవసరం లేదు)
            </Button>
            <div className="flex items-center gap-3"><div className="flex-1 h-px bg-goldline/40" /><span className="text-xs text-muted-foreground font-telugu">{t("or")}</span><div className="flex-1 h-px bg-goldline/40" /></div>
          </div>
        )}

        {(
          <div className="space-y-3">
            {mode === "signup" && (
              <div><Label className="font-telugu text-maroon">{t("full_name")}</Label>
                <Input data-testid="auth-name" value={form.name} onChange={set("name")} className="gold-border font-telugu" /></div>
            )}
            <div><Label className="font-telugu text-maroon">{t("email")}</Label>
              <Input data-testid="auth-email" type="email" value={form.email} onChange={set("email")} className="gold-border" /></div>
            {mode === "signup" && (
              <div><Label className="font-telugu text-maroon">{t("mobile")} <span className="text-xs text-muted-foreground">(ఐచ్ఛికం · OTP అవసరం లేదు)</span></Label>
                <Input data-testid="auth-mobile" value={form.mobile} onChange={set("mobile")} maxLength={10} className="gold-border" placeholder="10 అంకెలు" /></div>
            )}
            <div><Label className="font-telugu text-maroon">{t("password")}</Label>
              <Input data-testid="auth-password" type="password" value={form.password} onChange={set("password")} className="gold-border"
                onKeyDown={(e) => e.key === "Enter" && mode === "login" && doLogin()} /></div>

            <Button data-testid="auth-submit" onClick={mode === "login" ? doLogin : startSignup} disabled={busy} className="w-full btn-sacred border-0 font-telugu">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (mode === "login" ? t("login") : t("submit"))}
            </Button>

            {mode === "login" && (
              <p className="text-right text-sm -mt-1"><Link to="/forgot-password" data-testid="auth-forgot-link" className="text-saffron underline font-telugu">పాస్‌వర్డ్ మర్చిపోయారా?</Link></p>
            )}

            <p className="text-center text-sm text-muted-foreground font-telugu pt-2">
              {mode === "login" ? t("no_account") : t("already_account")}{" "}
              <button data-testid="auth-switch" className="text-saffron font-semibold underline" onClick={() => setMode(mode === "login" ? "signup" : "login")}>
                {mode === "login" ? t("signup") : t("login")}
              </button>
            </p>
          </div>
        )}
        <div className="mt-4 flex items-center justify-center gap-1 text-[11px] text-muted-foreground font-telugu"><ShieldCheck className="w-3 h-3" /> సురక్షితం & గోప్యం</div>
      </div>
    </div>
  );
}
